/**
 * Edit Template Modal
 * Allows editing content (body/header/footer text) of an APPROVED predefined template.
 * Template variables CANNOT be changed ” validation enforces this.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateTemplate,
  closeEditModal
} from '../store/slices/templateSettingsSlice';
import './EditTemplateModal.css';

// â”€â”€ helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function extractVariables(text) {
  if (!text) return [];
  return [...text.matchAll(/\{\{([^}]+)\}\}/g)].map(m => m[1].trim()).sort();
}

function getBodyText(waComponents) {
  if (!waComponents || !waComponents.length) return '';
  const body = waComponents.find(c => c.type === 'BODY');
  return body?.text || '';
}

function getHeaderText(waComponents) {
  if (!waComponents || !waComponents.length) return '';
  const header = waComponents.find(c => c.type === 'HEADER');
  return (header?.format === 'TEXT' || !header?.format) ? (header?.text || '') : '';
}

function getFooterText(waComponents) {
  if (!waComponents || !waComponents.length) return '';
  const footer = waComponents.find(c => c.type === 'FOOTER');
  return footer?.text || '';
}

function applyTextToComponents(waComponents, bodyText, headerText, footerText) {
  return (waComponents || []).map(comp => {
    if (comp.type === 'BODY') {
      return { ...comp, text: bodyText };
    }
    if (comp.type === 'HEADER' && (comp.format === 'TEXT' || !comp.format)) {
      return { ...comp, text: headerText };
    }
    if (comp.type === 'FOOTER') {
      return { ...comp, text: footerText };
    }
    return comp;
  });
}

const TYPE_COLORS = {
  INVOICE: '#4CAF50',
  INITIAL_MESSAGE: '#2196F3',
  WELCOME_MESSAGE: '#00BCD4',
  FEEDBACK_REQUEST: '#FF9800',
  PROFILE_UPDATE: '#9C27B0',
  APPOINTMENT_BOOKING: '#E91E63',
  PRODUCT: '#FF5722',
  IMAGE: '#607D8B'
};

// â”€â”€ component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const EditTemplateModal = ({ template, onClose }) => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector(state => state.templateSettings);

  const originalBodyText = getBodyText(template?.waComponents);
  const originalHeaderText = getHeaderText(template?.waComponents);
  const originalFooterText = getFooterText(template?.waComponents);
  const originalVars = extractVariables(originalBodyText);

  const [bodyText, setBodyText] = useState(originalBodyText);
  const [headerText, setHeaderText] = useState(originalHeaderText);
  const [footerText, setFooterText] = useState(originalFooterText);
  const [validationError, setValidationError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const hasTextHeader = !!getHeaderText(template?.waComponents) || 
    (template?.waComponents || []).some(c => c.type === 'HEADER' && (c.format === 'TEXT' || !c.format));
  const hasFooter = (template?.waComponents || []).some(c => c.type === 'FOOTER');

  // Validate body text ” variables must not change
  const validate = useCallback(() => {
    if (!bodyText.trim()) {
      setValidationError('Body text cannot be empty.');
      return false;
    }
    const newVars = extractVariables(bodyText);
    if (JSON.stringify(newVars) !== JSON.stringify(originalVars)) {
      setValidationError(
        `Template variables cannot be changed. Expected: ${
          originalVars.length ? originalVars.map(v => `{{${v}}}`).join(', ') : '(none)'
        }`
      );
      return false;
    }
    setValidationError('');
    return true;
  }, [bodyText, originalVars]);

  const handleSave = async () => {
    if (!validate()) return;

    const updatedComponents = applyTextToComponents(
      template.waComponents,
      bodyText,
      headerText,
      footerText
    );

    try {
      await dispatch(
        updateTemplate({
          templateSettingsId: template._id,
          updates: { waComponents: updatedComponents }
        })
      ).unwrap();
      setSuccessMessage('Template updated successfully');
      setTimeout(() => {
        dispatch(closeEditModal());
        if (onClose) onClose();
      }, 1200);
    } catch (err) {
      console.error('Failed to update template:', err);
    }
  };

  const typeColor = (type) => TYPE_COLORS[type] || '#757575';
  const typeLabel = (type) => {
    const map = {
      INVOICE: 'Invoice', INITIAL_MESSAGE: 'Initial Message',
      WELCOME_MESSAGE: 'Welcome', FEEDBACK_REQUEST: 'Feedback Request',
      PROFILE_UPDATE: 'Profile Update', APPOINTMENT_BOOKING: 'Appointment Booking'
    };
    return map[type] || (type || '').replace(/_/g, ' ');
  };

  return (
    <div className="modal-overlay" onClick={() => dispatch(closeEditModal())}>
      <div className="edit-template-modal" onClick={e => e.stopPropagation()}>
        {/* â”€â”€ Header â”€â”€ */}
        <div className="modal-header">
          <div className="header-content">
            <div
              className="template-type-badge"
              style={{ backgroundColor: typeColor(template?.templateType) }}
            >
              {typeLabel(template?.templateType).charAt(0)}
            </div>
            <div>
              <h2>Edit Template</h2>
              <p className="template-type-label">{typeLabel(template?.templateType)}</p>
            </div>
          </div>
          <button
            className="close-btn"
            onClick={() => dispatch(closeEditModal())}
            aria-label="Close"
          >
            âœ•
          </button>
        </div>

        {/* â”€â”€ Body â”€â”€ */}
        <div className="modal-body">
          {successMessage && (
            <div className="success-banner"><span>âœ“ {successMessage}</span></div>
          )}
          {(error || validationError) && (
            <div className="error-banner">
              <span>{validationError || error?.message || 'An error occurred'}</span>
            </div>
          )}

          {/* Notice */}
          <div className="etm-notice">
            <strong>Note:</strong> You can edit text content only. Template variables (
            {originalVars.length
              ? originalVars.map(v => <code key={v}>{`{{${v}}}`}</code>).reduce((a, b) => [a, ', ', b])
              : 'none'
            }) must remain unchanged.
          </div>

          {/* Header text */}
          {hasTextHeader && (
            <div className="section">
              <h3>Header Text</h3>
              <div className="form-group">
                <textarea
                  value={headerText}
                  onChange={e => setHeaderText(e.target.value)}
                  placeholder="Header text (max 60 chars)"
                  rows={2}
                  maxLength={60}
                />
                <small className="char-count">{headerText.length}/60</small>
              </div>
            </div>
          )}

          {/* Body text */}
          <div className="section">
            <h3>Body Text <span className="required">*</span></h3>
            <div className="form-group">
              <textarea
                value={bodyText}
                onChange={e => {
                  setBodyText(e.target.value);
                  setValidationError('');
                }}
                placeholder="Message body text"
                rows={6}
                maxLength={1024}
              />
              <small className="char-count">{bodyText.length}/1024</small>
            </div>
            {/* Variable chips */}
            {originalVars.length > 0 && (
              <div className="etm-vars">
                <span className="etm-vars-label">Variables (read-only):</span>
                {originalVars.map(v => (
                  <span key={v} className="etm-var-chip">{`{{${v}}}`}</span>
                ))}
              </div>
            )}
          </div>

          {/* Footer text */}
          {hasFooter && (
            <div className="section">
              <h3>Footer Text</h3>
              <div className="form-group">
                <textarea
                  value={footerText}
                  onChange={e => setFooterText(e.target.value)}
                  placeholder="Footer text (max 60 chars)"
                  rows={2}
                  maxLength={60}
                />
                <small className="char-count">{footerText.length}/60</small>
              </div>
            </div>
          )}

          {/* Read-only components preview (non-text parts) */}
          {(template?.waComponents || []).some(c => ['BUTTONS'].includes(c.type)) && (
            <div className="section">
              <h3>Buttons <span className="etm-readonly-label">(read-only)</span></h3>
              {(template.waComponents.find(c => c.type === 'BUTTONS')?.buttons || []).map((btn, i) => (
                <div key={i} className="button-preview">
                  <span className="button-type">{btn.type}</span>
                  <span className="button-text">{btn.text}</span>
                  {btn.url && <span className="button-url">{btn.url}</span>}
                </div>
              ))}
            </div>
          )}

          {/* Metadata */}
          <div className="section">
            <h3>Metadata</h3>
            <div className="metadata-item">
              <span className="label">Status:</span>
              <span className="value etm-status-approved">{template?.status}</span>
            </div>
            <div className="metadata-item">
              <span className="label">Usage Count:</span>
              <span className="value">{template?.usageCount || 0} times</span>
            </div>
            {template?.metaTemplateId && (
              <div className="metadata-item">
                <span className="label">Meta ID:</span>
                <span className="value etm-mono">{template.metaTemplateId}</span>
              </div>
            )}
          </div>
        </div>

        {/* â”€â”€ Footer â”€â”€ */}
        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={() => dispatch(closeEditModal())}
            disabled={loading}
          >
            Cancel
          </button>
          <div className="footer-spacer" />
          <button
            className="btn btn-success"
            onClick={handleSave}
            disabled={loading || !bodyText.trim()}
          >
            {loading ? 'Saving¦' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditTemplateModal;
